import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../styles/popup.scss';
import useModelManagementStore from '../store/useModelManagementStore';
import useModelManagementHook from '../hooks/useModelManagementHook';
import { useSwal } from '../../../../components/Notifications/swal/SwalCom';
import { getLoggedInOwnerId } from '../../../../utils/auth';

const ModelPopup = ({ isModalOpen, handleCloseModal }) => {
  const {
    modelName,
    setModelName,
    modelDetailInputField,
    setModelDetailInputField,
    modelInfo,
    modelType,
    setModelType,
    projectId: activeProjectId,
    formProjectId,
    projectDD,
    setFormProjectId,
    visibility,
    setVisibility,
    tags,
    setTags,
  } = useModelManagementStore();

  const { handler } = useModelManagementHook();
  const {
    handleGetProjectsDD,
    handleResetAddPopup,
    handleEditModel,
    handleSaveModel,
  } = handler;
  const navigate = useNavigate();
  const { swalWarning, swalFail } = useSwal();

  const isEditMode = useMemo(() => Boolean(modelInfo?.model_id), [modelInfo]);

  // Hydrate the form once per open. Re-running it on any later store change would
  // discard whatever the user has typed/picked so far.
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!isModalOpen) {
      hydratedRef.current = false;
      return;
    }
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    setModelName(modelInfo?.model_name || '');
    setModelDetailInputField(modelInfo?.model_detail || '');
    setModelType(modelInfo?.model_type || '');
    setVisibility(modelInfo?.visibility || 'private');
    setTags(Array.isArray(modelInfo?.tags) ? modelInfo.tags.join(', ') : (modelInfo?.tags || ''));
    setFormProjectId(
      modelInfo?.project_id || modelInfo?.projectId || activeProjectId || '',
    );

    handleGetProjectsDD();
  }, [activeProjectId, handleGetProjectsDD, isModalOpen, modelInfo, setFormProjectId, setModelDetailInputField, setModelName, setModelType, setVisibility, setTags]);

  const closeModal = () => {
    handleResetAddPopup();
    handleCloseModal();
  };

  const handleSave = async () => {
    const trimmedName = modelName?.trim();
    if (!trimmedName) {
      swalWarning('Missing name', 'Please enter a model name.');
      return;
    }

    if (!modelType) {
      swalWarning('Missing type', 'Please select a model type.');
      return;
    }

    if (!isEditMode && !formProjectId) {
      swalWarning('Missing project', 'Please select the project this model belongs to.');
      return;
    }

    const ownerId = getLoggedInOwnerId();
    if (!ownerId) {
      swalFail('Missing user', 'Unable to determine logged in user. Please login again.');
      return;
    }

    const formData = new FormData();
    formData.append('name', trimmedName);
    formData.append('detail', modelDetailInputField || '');
    formData.append('model_type', modelType);
    formData.append('owner_id', ownerId);
    formData.append('project_id', formProjectId || '');
    formData.append('visibility', visibility || 'private');
    formData.append('tags', tags || '');

    if (modelInfo?.model_id) {
      formData.append('id', modelInfo.model_id);
    }

    const result = isEditMode
      ? await handleEditModel(formData)
      : await handleSaveModel(formData);

    if (!result?.success) {
      if (result?.message) {
        swalFail('Save failed', result.message);
      } else {
        swalFail('Save failed', 'Could not save model. Please try again.');
      }
      return;
    }

    if (!isEditMode) {
      const savedModel = result.data || modelInfo;
      const mt = (savedModel?.modelType || modelType || '').toLowerCase();
      const target =
        mt === 'classification' ? '/ClassificationLabel'
          : mt === 'segmentation' ? '/SegmentationLabel'
          : '/DetectionLabel';
      const payload = savedModel
        ? { ...savedModel, visibility: savedModel.visibility ?? visibility ?? 'private' }
        : { model_type: modelType, visibility: visibility ?? 'private' };
      navigate(target, { state: { model: payload } });
    }

    closeModal();
  };

  if (!isModalOpen) {
    return null;
  }

  return (
    <div
      className="modal fade show"
      tabIndex="-1"
      role="dialog"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title h4">
              <i className={`fa ${isEditMode ? 'fa-edit' : 'fa-plus'}`}></i>{' '}
              {isEditMode ? 'Edit Model' : 'Add New Model'}
            </h5>
            <button type="button" className="close" onClick={closeModal}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <form className="model-popup-form">
              <div className="form-group">
                <label className="col-form-label">Model name:</label>
                <input
                  type="text"
                  className="form-control"
                  value={modelName || ''}
                  onChange={(e) => setModelName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="col-form-label">Model detail:</label>
                <textarea
                  className="form-control"
                  value={modelDetailInputField || ''}
                  onChange={(e) => setModelDetailInputField(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="col-form-label">Tags:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. yolov8, retail, defects"
                  value={tags || ''}
                  onChange={(e) => setTags(e.target.value)}
                />
                <small className="form-text text-muted">
                  Comma-separated. Shown as filter chips in the Public Model Hub.
                </small>
              </div>

              {!isEditMode && (
                <>
                  <div className="form-group">
                    <label className="col-form-label">Under which Project:</label>
                    <select
                      className="form-control"
                      value={formProjectId || ''}
                      onChange={(e) => setFormProjectId(e.target.value)}
                    >
                      <option value="">-- Select Project --</option>
                      {projectDD?.map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="col-form-label">Model Type:</label>
                    <select
                      className="form-control"
                      value={modelType || ''}
                      onChange={(e) => setModelType(e.target.value)}
                    >
                      <option value="">-- Select Type --</option>
                      <option value="classification">Classification</option>
                      <option value="bounding_box">Bounding Box</option>
                      <option value="segmentation">Segmentation</option>
                    </select>
                  </div>
                </>
              )}

              <div className="form-group visibility-segment-wrap">
                <div className="visibility-segment-label">
                  <span>Visibility</span>
                  <button
                    type="button"
                    className="visibility-info-btn"
                    title="Click for visibility details"
                    onClick={() => {
                      Swal.fire({
                        title: 'Visibility',
                        html: `
                          <p><strong>Public</strong> – Free training. Your model appears in the Public Model Hub for others to discover and download.</p>
                          <p><strong>Private</strong> – $5 training fee. Model stays in your project only. Payment is required when you click Train after labeling.</p>
                        `,
                        icon: 'info',
                        confirmButtonText: 'Got it',
                      });
                    }}
                  >
                    <i className="fa fa-info" aria-hidden="true" />
                  </button>
                </div>
                <div className="visibility-segment-control">
                  <button
                    type="button"
                    className={`visibility-segment-option visibility-public ${visibility === 'public' ? 'is-active' : ''}`}
                    onClick={() => setVisibility('public')}
                  >
                    <i className="fa fa-globe" aria-hidden="true" />
                    <span>Public</span>
                    <small>Free training · Model Hub</small>
                  </button>
                  <button
                    type="button"
                    className={`visibility-segment-option visibility-private ${visibility === 'private' ? 'is-active' : ''}`}
                    onClick={() => setVisibility('private')}
                  >
                    <i className="fa fa-lock" aria-hidden="true" />
                    <span>Private</span>
                    <small>$5 training · Project only</small>
                  </button>
                </div>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>
              {isEditMode ? 'Update' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelPopup;
